import { useState, useEffect } from 'react';
import Button from '../base/Button';
import { useAppConfig } from '../../hooks/useAppConfig';

export default function Download() {
  const appConfig = useAppConfig();
  const [imageLoaded, setImageLoaded] = useState(false);
  
  useEffect(() => {
    // Preload the image
    const img = new Image();
    img.src = appConfig.downloadBackgroundImage;
    img.onload = () => {
      setImageLoaded(true);
    };
  }, [appConfig.downloadBackgroundImage]);
  
  return (
    <section id="download" className="py-20 relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${appConfig.gradientFrom.replace('0.9', '1')} 0%, ${appConfig.gradientTo.replace('0.8', '1')} 100%)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Background image layer with fade-in animation */}
      <div 
        className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
        style={{
          backgroundImage: `url('${appConfig.downloadBackgroundImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: imageLoaded ? 1 : 0
        }}
      ></div>
      <div className="absolute inset-0 bg-black/10"></div>
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h3 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Download <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">{appConfig.name}</span> Today
          </h3>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
            Join thousands of users who have transformed their devices with premium 4K wallpapers. 
            {appConfig.appStoreUrl ? ' Available for both iOS and Android devices.' : ' Available for Android devices.'}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
          {appConfig.appStoreUrl && (
            <Button 
              href={appConfig.appStoreUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              size="lg"
              className="bg-black hover:bg-gray-800 text-white flex items-center space-x-4 px-8 py-4"
            >
              <i className="ri-apple-fill text-3xl"></i>
              <div className="text-left">
                <div className="text-sm opacity-80">Download on the</div>
                <div className="text-lg font-semibold">App Store</div>
              </div>
            </Button>
          )}
          
          <Button 
            href={appConfig.playStoreUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            size="lg"
            className="bg-black hover:bg-gray-800 text-white flex items-center space-x-4 px-8 py-4"
          >
            <i className="ri-google-play-fill text-3xl"></i>
            <div className="text-left">
              <div className="text-sm opacity-80">Get it on</div>
              <div className="text-lg font-semibold">Google Play</div>
            </div>
          </Button>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 text-center text-white">
          <div>
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-check-line text-2xl"></i>
            </div>
            <h4 className="text-lg font-semibold mb-2">100% Safe & Secure</h4>
            <p className="text-white/80">Verified by app stores and trusted by thousands of users worldwide</p>
          </div>
          
          <div>
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-download-cloud-line text-2xl"></i>
            </div>
            <h4 className="text-lg font-semibold mb-2">Free Download</h4>
            <p className="text-white/80">Start with thousands of free wallpapers, premium content available</p>
          </div>
          
          <div>
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-refresh-line text-2xl"></i>
            </div>
            <h4 className="text-lg font-semibold mb-2">Regular Updates</h4>
            <p className="text-white/80">New wallpapers added weekly with latest trends and designs</p>
          </div>
        </div>
      </div>
      
      {/* Background decoration */}
      <div className="absolute top-20 left-10 w-32 h-32 rounded-full blur-3xl"
        style={{ backgroundColor: `${appConfig.primaryColor}33` }}></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full blur-3xl"
        style={{ backgroundColor: `${appConfig.secondaryColor}33` }}></div>
    </section>
  );
}
